// Import analytics utilities
import { Analytics, initScrollDepthTracking, initSectionTracking, initTimeTracking } from './analytics';

// Type definitions
// Declare Turnstile global
declare const turnstile: {
  reset: (container?: string | HTMLElement) => void;
};

interface ContactFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  website?: string;
  turnstileToken?: string;
}

interface ProjectData {
  id: string;
  title: string;
  description: string;
  longDescription: string;
  technologies: string[];
  features: string[];
  challenges: string[];
  outcomes: string[];
  liveDemo?: string;
  githubRepo?: string;
  images: string[];
}

class PortfolioSite {
  private navbar: HTMLElement;
  private heroSection: HTMLElement;
  private contactForm: HTMLFormElement;
  private projectModal: HTMLElement;
  private projectData: ProjectData[];

  constructor() {
    this.navbar = document.querySelector('.navbar') as HTMLElement;
    this.heroSection = document.querySelector('.hero-section') as HTMLElement;
    this.contactForm = document.querySelector('#contactForm') as HTMLFormElement;
    this.projectModal = document.querySelector('#projectModal') as HTMLElement;

    this.projectData = this.initializeProjectData();
    this.init();
  }

  private initializeProjectData(): ProjectData[] {
    return [
      {
        id: 'jobcatcher',
        title: 'JobCatcher',
        description: 'Automated job search assistant that scrapes LinkedIn, Indeed, and Eluta, scores jobs with AI, and surfaces them in a unified dashboard with per-application chat, tailored resumes, and cover letters.',
        longDescription: 'JobCatcher is an end-to-end automated job-search assistant. Users configure their profile, resume, search criteria, and scoring weights, then JobCatcher scrapes LinkedIn, Indeed, and Eluta via n8n and Apify, scores each posting with AI, and surfaces results in a unified tracking dashboard. Each application supports threaded Q&A chat, server-side PDF generation of tailored resumes and cover letters, full version history, and reach-out message drafting. A wallet system meters every run, chat, and AI call, while an admin dashboard exposes user management, run drill-downs, and cost auditing.',
        technologies: ['React 19', 'TypeScript', 'Vite', 'SCSS', 'Express 5', 'Node 22', 'Supabase', 'PostgreSQL', 'n8n', 'Apify', 'OpenRouter', 'Puppeteer', 'Recharts', 'GCP Cloud Run', 'Sentry', 'PostHog'],
        features: [
          'Marketing homepage with Privacy Policy and Terms pages',
          '7-step config wizard: profile, resume, search, scoring weights, cover letter, advanced (BYOK key), review',
          '3-pane Manage Jobs view with filter rail, dense table, detail panel, bulk ops, and server-side sort/search/filter',
          'Per-application chat with custom instructions, optimistic messages, model-change markers, and wallet-debited usage',
          'AI-generated cover letters and tailored resumes as PDFs with full version history (DeepSeek V3 primary, Grok fallback)',
          'Analytics dashboard: status distribution, funnel, timeline, weekly progress, platform breakdown, rating outcomes',
          'Run history with incremental Apify cost reporting, OpenRouter delta tracking, progress stepper, and scheduled runs',
          'Per-user wallet with debits per run/chat/AI call plus admin audit and manual adjustments',
          'Admin dashboard with Profile/Wallet/Runs detail tabs, user management, and cost auditing',
          'HTML email notifications (MailerSend) for jobs found, no results, and run failures',
          'Permanent Supabase Storage hosting for company logos (resolves expiring LinkedIn CDN URLs)',
          'Ghost detection cron that auto-marks stale applications as Ghosted',
          'Mobile-first responsive UI with hover-expand sidebar, 3-way theme toggle, and WCAG AA contrast',
          'Security hardening: Helmet, rate limiting, webhook IP allowlist + idempotency, AES-256-GCM key encryption with rotation tooling, email change verification, and RLS on all tables'
        ],
        challenges: [
          'Coordinating multi-platform scraping (LinkedIn, Indeed, Eluta) through a single n8n + Apify pipeline',
          'Designing a fair wallet model that meters runs, chats, and AI calls without surprising users',
          'Generating high-quality tailored resumes and cover letters as PDFs with full version history',
          'Enforcing tenant isolation and key security across Supabase RLS, encrypted BYOK keys, and webhook idempotency',
          'Keeping the Manage Jobs view fast and ergonomic with server-side sort, search, filter, and bulk operations'
        ],
        outcomes: [
          'Shipped a full marketing site, config wizard, tracking dashboard, chat, and admin console as one cohesive product',
          'Reduced manual job-search overhead via AI-scored postings and one-click tailored resume + cover letter generation',
          'Built a transparent, audited cost model with per-user wallets and admin manual adjustments',
          'Achieved a mobile-first, WCAG-AA UI with 3-way theme toggle across every user-facing page',
          'Deployed to GCP Cloud Run with automated Cloud Build, Cloud Scheduler crons, Sentry monitoring, and PostHog analytics'
        ],
        liveDemo: 'https://jobs.seifi.dev',
        images: ['/src/assets/jobcatcher.png']
      },
      {
        id: 'galerie-maya',
        title: 'Galerie Maya',
        description: 'Robust Shopify e-commerce platform with modern design and comprehensive SEO optimization.',
        longDescription: 'This project involved designing and implementing a robust Shopify website for Galerie Maya—a modern online store that not only showcases curated products but also streamlines the purchasing process. The site features a clean, user-centric design paired with full ecommerce functionality, including secure checkout and Shopify\'s inventory management system.',
        technologies: ['Shopify', 'JavaScript', 'CSS', 'SEO Optimization', 'Inventory Management'],
        features: [
          'Fully operational Shopify e-commerce platform',
          'Modern, intuitive interface design',
          'Advanced inventory management features',
          'SEO best practices implementation',
          'Streamlined product addition system',
          'Secure checkout process',
          'Mobile-responsive design'
        ],
        challenges: [
          'Integrating Shopify\'s backend with custom design requirements',
          'Optimizing product showcase while maintaining fast loading times',
          'Implementing comprehensive SEO strategies',
          'Creating efficient store management workflows'
        ],
        outcomes: [
          'Launched a fully operational Shopify e-commerce platform that enhanced customer engagement',
          'Designed and implemented a modern, intuitive interface, improving user experience and conversion rates',
          'Integrated advanced inventory management features through Shopify\'s backend',
          'Optimized the website with SEO best practices, resulting in increased organic traffic and improved search rankings',
          'Streamlined product addition and overall store management for efficient day-to-day operations'
        ],
        liveDemo: 'https://galeriemaya.com/',
        images: ['/src/assets/galerie-maya.avif']
      },
      {
        id: 'la-caviare',
        title: 'La Caviare – Persian Farmed Caviar',
        description: 'Luxurious one-page website showcasing premium Persian Beluga caviars with minimalist design.',
        longDescription: 'Developed on Squarespace, this project presents a luxurious and refined online presence for La Caviare. The one-page design highlights detailed information about the exquisite Persian Beluga caviars while maintaining a minimalist aesthetic. The site is purpose-built to allow users to easily inquire about purchases via an integrated contact form, and strategic SEO implementations help boost its online presence in a competitive niche market.',
        technologies: ['Squarespace', 'Custom CSS', 'SEO', 'Contact Forms', 'Responsive Design'],
        features: [
          'Sleek, minimalist one-page website design',
          'Integrated inquiry form for customer interaction',
          'Targeted SEO techniques implementation',
          'High-end branding through thoughtful content layout',
          'Luxury aesthetic with functional performance',
          'Mobile-optimized experience',
          'Lead generation optimization'
        ],
        challenges: [
          'Conveying luxury brand essence through digital design',
          'Balancing minimalist aesthetic with informative content',
          'Optimizing for niche market SEO competition',
          'Creating seamless inquiry-to-purchase flow'
        ],
        outcomes: [
          'Delivered a sleek, minimalist one-page website that effectively communicates the brand\'s luxury ethos',
          'Integrated a streamlined inquiry form to enhance customer interaction and lead generation',
          'Implemented targeted SEO techniques, resulting in improved search engine visibility',
          'Successfully conveyed high-end branding through thoughtful content layout and design simplicity',
          'Enhanced overall user engagement by focusing on both aesthetic appeal and functional performance'
        ],
        liveDemo: 'https://lacaviare.ca/',
        images: ['/src/assets/la-caviare.avif']
      },
      {
        id: 'eluta-scraper',
        title: 'Eluta Scraper',
        description: 'Apify actor that scrapes Canadian job listings from Eluta.ca and returns clean, structured records ready for ATS automation, AI scoring, resume matching, or a personal job feed.',
        longDescription: 'Eluta Scraper is a published Apify actor that crawls Eluta.ca for Canadian job postings and emits structured, deduplicated records for downstream automation. For each posting it captures the full job description (markdown / HTML / text), resolves Eluta\'s redirect to the real employer ATS URL (Workday, Greenhouse, Rippling, etc.), and extracts structured fields including salary when published, employment type, remote/hybrid/onsite signal, employer URL, and city/province/country. A built-in 90-day repost memory means cron jobs only pay for genuinely new postings.',
        technologies: ['TypeScript', 'Node.js 20+', 'Apify SDK', 'Crawlee', 'Cheerio', 'Turndown', 'Vitest'],
        features: [
          'Multi-query input with per-query pagination and result caps',
          'Location filters by city and two-letter province code',
          'daysOld filter that stops pagination once results pre-date the window',
          'Optional full-detail mode: ~5k-char descriptions, resolved apply URLs, structured fields',
          'Cheaper listings-only mode for high-volume sweeps',
          'Resolved apply URLs to real employer ATS portals (Workday, Greenhouse, Rippling, Deloitte, etc.)',
          'Structured salary, employmentType, industry, and remoteType (ONSITE/HYBRID/REMOTE/UNKNOWN)',
          'Schema.org JSON-LD extraction for validThrough, companyUrl, and salary when published',
          '90-day repost memory with skipReposts flag — cron jobs only pay for genuinely new postings',
          'Stable Eluta GUID (`id`) for dedup plus `contentHash` for edit detection',
          'Configurable concurrency and optional Apify Proxy routing',
          'Pay-per-event pricing: $0.99 per 1,000 listings-only, $1.99 per 1,000 enriched records'
        ],
        challenges: [
          'Resolving Eluta\'s redirect URLs to real employer ATS endpoints across many vendor patterns',
          'Reliably extracting salary, employment type, and remote signal from inconsistent posting HTML and JSON-LD',
          'Designing a 90-day repost-memory store so daily cron runs avoid paying twice for the same posting',
          'Balancing throughput against polite crawling with configurable concurrency'
        ],
        outcomes: [
          'Published on the Apify Store with transparent pay-per-event pricing',
          'Returns clean, deduplicated records ready to feed ATS automation, AI scoring, resume matching, or a personal job feed',
          'Powers the Eluta data source for JobCatcher',
          'Supports both cheap listings-only sweeps and full enriched extraction in a single actor'
        ],
        liveDemo: 'https://apify.com/seifi/eluta-scraper',
        images: ['/src/assets/eluta-scraper.svg']
      },
      {
        id: 'multi-tenant-auth',
        title: 'Multi-Tenant Authentication Platform',
        description: 'Production-ready SaaS authentication system with complete tenant isolation, branded UI components, and AWS deployment.',
        longDescription: 'A production-ready multi-tenant SaaS authentication system built from scratch, featuring complete tenant isolation, JWT authentication with role-based access control, and a branded UI component library. The platform implements row-level security in PostgreSQL, tenant-specific routing, and comprehensive testing with automated CI/CD deployment to AWS infrastructure.',
        technologies: ['Node.js', 'Express', 'React', 'TypeScript', 'PostgreSQL', 'Docker', 'AWS', 'GitHub Actions', 'styled-components', 'Jest', 'Vitest'],
        features: [
          'JWT authentication with access and refresh tokens',
          'Role-based access control (Admin, User, Viewer)',
          'Complete tenant isolation with row-level security',
          'Tenant-specific URL routing (/t/:tenantSlug)',
          'Branded UI component library with dynamic theming',
          'Account lockout and rate limiting for security',
          'Comprehensive testing (unit, integration, accessibility)',
          'Dockerized deployment with CI/CD pipeline',
          'AWS infrastructure (EC2, RDS, Nginx, SSL)'
        ],
        challenges: [
          'Implementing secure multi-tenant data isolation',
          'Building accessible, themeable UI components from scratch',
          'Designing scalable JWT refresh token strategy',
          'Setting up production-ready AWS infrastructure with automated deployment'
        ],
        outcomes: [
          'Built a complete multi-tenant authentication system demonstrating deep understanding of IAM and security principles',
          'Implemented production-grade security features including account lockout, rate limiting, and tenant isolation',
          'Created an accessible UI component library with WCAG 2.1 compliance and dynamic theming capabilities',
          'Deployed to AWS with Docker, Nginx, SSL, and automated CI/CD pipeline using GitHub Actions',
          'Achieved comprehensive test coverage with Jest, Supertest, Vitest, and React Testing Library'
        ],
        githubRepo: 'https://github.com/majidseifi/multi-tenant-auth-platform',
        images: ['/src/assets/multi-tenant-auth.svg']
      },
      {
        id: 'agrisphere',
        title: 'AgriSphere – Agri-Tourism Platform',
        description: 'Agri-tourism platform with AI-enhanced search, developed for Seneca Hackathon 2025. Pre-production prototype that evolved into Sowcial.',
        longDescription: 'AgriSphere is an agri-tourism platform developed during Seneca Hackathon 2025, and the pre-production prototype that evolved into Sowcial. The project was crafted using Tailwind CSS alongside custom HTML and JavaScript to create a modern, responsive front-end that highlights immersive farm experiences—from day tours and overnight farm stays to hands-on workshops. The backend is built on MongoDB and powered by an AI-enhanced search engine utilizing Flask, Werkzeug, FastAPI, Uvicorn, Python-dotenv, OpenAI, and Pydantic.',
        technologies: ['Tailwind CSS', 'HTML', 'JavaScript', 'MongoDB', 'Flask', 'FastAPI', 'OpenAI', 'Python', 'Uvicorn', 'Pydantic'],
        features: [
          'Modern, responsive front-end with Tailwind CSS',
          'AI-enhanced search engine integration',
          'Distinct interfaces for members and farmers',
          'Seamless booking flows',
          'Personalized dashboards',
          'Automatic farmer onboarding system',
          'Immersive farm experience showcase',
          'Real-time availability management'
        ],
        challenges: [
          'Rapid development within hackathon timeframe',
          'Integrating AI search capabilities with farm data',
          'Creating dual user interfaces for different user types',
          'Balancing feature richness with development speed'
        ],
        outcomes: [
          'Successfully built and launched a full-stack platform within the competitive timeframe of the Seneca Hackathon 2025',
          'Utilized Tailwind CSS and custom HTML/JS to deliver a sleek, responsive design that enhances user engagement',
          'Employed MongoDB for data management alongside an AI-driven search engine leveraging Flask, FastAPI, and OpenAI to deliver fast, accurate results',
          'Developed distinct interfaces for both members and farmers, including seamless booking flows, personalized dashboards, and an automatic farmer onboarding system',
          'Bridged the gap between digital interaction and tangible farm experiences, reinforcing sustainable practices and deeper connections between consumers and producers'
        ],
        liveDemo: 'https://agrisphere.vercel.app/',
        githubRepo: '#',
        images: ['/src/assets/agrisphere.avif']
      }
    ];
  }

  private init(): void {
    this.setupScrollEffects();
    this.setupSmoothScrolling();
    this.setupContactForm();
    this.setupAnimations();
    this.setupProjectModal();
    this.initializeAnalytics();
  }

  private initializeAnalytics(): void {
    // Initialize enhanced analytics tracking
    initScrollDepthTracking();
    initSectionTracking();
    initTimeTracking();
    this.setupAnalyticsEventListeners();
  }

  private setupAnalyticsEventListeners(): void {
    // Track navigation clicks
    const navLinks = document.querySelectorAll('.navbar a.nav-link');
    navLinks.forEach(link => {
      link.addEventListener('click', () => {
        const linkText = link.textContent?.trim() || '';
        const destination = link.getAttribute('href') || '';
        Analytics.trackNavigation(linkText, destination);
      });
    });

    // Track CTA button clicks
    const ctaButtons = document.querySelectorAll('.hero-cta .btn');
    ctaButtons.forEach(button => {
      button.addEventListener('click', () => {
        const ctaText = button.textContent?.trim() || '';
        Analytics.trackCTAClick(ctaText, 'hero_section');
      });
    });

    // Track social media clicks
    const socialLinks = document.querySelectorAll('.social-links a, .hero-contact-info a');
    socialLinks.forEach(link => {
      link.addEventListener('click', () => {
        const linkUrl = link.getAttribute('href') || '';
        const linkText = link.textContent?.trim() || link.getAttribute('aria-label') || '';

        // Determine platform from URL or text
        let platform = 'unknown';
        if (linkUrl.includes('linkedin')) platform = 'LinkedIn';
        else if (linkUrl.includes('github')) platform = 'GitHub';
        else if (linkUrl.includes('twitter') || linkUrl.includes('x.com')) platform = 'X/Twitter';
        else if (linkUrl.includes('instagram')) platform = 'Instagram';
        else if (linkUrl.includes('mailto')) platform = 'Email';

        if (platform === 'Email' || linkUrl.includes('linkedin') || linkUrl.includes('github')) {
          Analytics.trackSocialClick(platform);
        } else {
          Analytics.trackExternalLink(linkUrl, linkText, 'social_media');
        }
      });
    });

    // Track external project links
    const projectLinks = document.querySelectorAll('.project-card a[target="_blank"]');
    projectLinks.forEach(link => {
      link.addEventListener('click', () => {
        const linkUrl = link.getAttribute('href') || '';
        const linkText = link.textContent?.trim() || '';
        Analytics.trackExternalLink(linkUrl, linkText, 'project_card');
      });
    });
  }

  private setupScrollEffects(): void {
    window.addEventListener('scroll', () => {
      this.handleNavbarScroll();
      this.handleParallaxEffect();
    });
  }

  private handleNavbarScroll(): void {
    const scrollTop = window.pageYOffset;
    
    if (scrollTop > 100) {
      this.navbar.style.backgroundColor = 'rgba(0, 0, 0, 0.98)';
      this.navbar.style.boxShadow = '0 2px 20px rgba(0, 0, 0, 0.5)';
    } else {
      this.navbar.style.backgroundColor = 'rgba(0, 0, 0, 0.95)';
      this.navbar.style.boxShadow = 'none';
    }

    // Handle summary section blur effect
    this.handleSummaryBlur();
  }

  private handleSummaryBlur(): void {
    const scrollTop = window.pageYOffset;
    const summarySection = document.querySelector('#summary') as HTMLElement;
    const heroSection = document.querySelector('.hero-section') as HTMLElement;
    
    if (summarySection && heroSection) {
      const summaryTop = summarySection.offsetTop;
      const windowHeight = window.innerHeight;
      
      // Start blurring the hero background when user scrolls toward summary section
      if (scrollTop > (summaryTop - windowHeight * 0.8)) {
        heroSection.classList.add('blurred');
      } else {
        heroSection.classList.remove('blurred');
      }
    }
  }

  private handleParallaxEffect(): void {
    const scrollTop = window.pageYOffset;
    const heroElements = this.heroSection.querySelectorAll('.hero-title, .hero-subtitle, .hero-cta');
    
    heroElements.forEach((element, index) => {
      const speed = 0.5 + (index * 0.1);
      const yPos = -(scrollTop * speed);
      (element as HTMLElement).style.transform = `translateY(${yPos}px)`;
    });
  }

  private setupSmoothScrolling(): void {
    const navLinks = document.querySelectorAll('a[href^="#"]');
    
    navLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const href = link.getAttribute('href');
        if (href) {
          const targetElement = document.querySelector(href);
          if (targetElement) {
            const offsetTop = (targetElement as HTMLElement).offsetTop - 80;
            window.scrollTo({
              top: offsetTop,
              behavior: 'smooth'
            });
          }
        }
      });
    });
  }

  private setupContactForm(): void {
    this.contactForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleFormSubmission();
    });

    // Add real-time validation
    const inputs = this.contactForm.querySelectorAll('input, textarea');
    inputs.forEach(input => {
      input.addEventListener('blur', () => {
        this.validateField(input as HTMLInputElement | HTMLTextAreaElement);
      });

      // Track form field interactions
      input.addEventListener('focus', () => {
        Analytics.trackFormFieldInteraction((input as HTMLInputElement).id);
      });
    });
  }

  private handleFormSubmission(): void {
    const formData: ContactFormData = {
      name: (document.getElementById('name') as HTMLInputElement).value,
      email: (document.getElementById('email') as HTMLInputElement).value,
      subject: (document.getElementById('subject') as HTMLInputElement).value,
      message: (document.getElementById('message') as HTMLTextAreaElement).value,
      website: (document.getElementById('website') as HTMLInputElement)?.value || '',
      turnstileToken: (document.querySelector('[name="cf-turnstile-response"]') as HTMLInputElement)?.value || ''
    };

    if (this.validateForm(formData)) {
      this.submitForm(formData);
    }
  }

  private validateForm(data: ContactFormData): boolean {
    let isValid = true;
    const errors: string[] = [];

    // Name validation
    if (!data.name.trim()) {
      errors.push('Name is required');
      isValid = false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!data.email.trim() || !emailRegex.test(data.email)) {
      errors.push('Valid email is required');
      isValid = false;
    }

    // Subject validation
    if (!data.subject.trim()) {
      errors.push('Subject is required');
      isValid = false;
    }

    // Message validation
    if (!data.message.trim() || data.message.length < 10) {
      errors.push('Message must be at least 10 characters');
      isValid = false;
    }

    if (!isValid) {
      this.showFormErrors(errors);
    }

    return isValid;
  }

  private validateField(field: HTMLInputElement | HTMLTextAreaElement): void {
    const value = field.value.trim();
    let isValid = true;
    let errorMessage = '';

    switch (field.id) {
      case 'name':
        if (!value) {
          isValid = false;
          errorMessage = 'Name is required';
        }
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value || !emailRegex.test(value)) {
          isValid = false;
          errorMessage = 'Valid email is required';
        }
        break;
      case 'subject':
        if (!value) {
          isValid = false;
          errorMessage = 'Subject is required';
        }
        break;
      case 'message':
        if (!value || value.length < 10) {
          isValid = false;
          errorMessage = 'Message must be at least 10 characters';
        }
        break;
    }

    this.toggleFieldError(field, isValid, errorMessage);
  }

  private toggleFieldError(field: HTMLInputElement | HTMLTextAreaElement, isValid: boolean, errorMessage: string): void {
    const existingError = field.parentElement?.querySelector('.error-message');
    
    if (existingError) {
      existingError.remove();
    }

    if (!isValid) {
      field.style.borderColor = '#dc3545';
      const errorDiv = document.createElement('div');
      errorDiv.className = 'error-message text-danger small mt-1';
      errorDiv.textContent = errorMessage;
      field.parentElement?.appendChild(errorDiv);
    } else {
      field.style.borderColor = '';
    }
  }

  private showFormErrors(errors: string[]): void {
    // Remove existing error messages
    const existingErrors = this.contactForm.querySelectorAll('.error-message');
    existingErrors.forEach(error => error.remove());

    // Show new errors
    const errorContainer = document.createElement('div');
    errorContainer.className = 'alert alert-danger mt-3';
    errorContainer.innerHTML = errors.map(error => `<div>${error}</div>`).join('');
    
    this.contactForm.appendChild(errorContainer);

    // Remove error message after 5 seconds
    setTimeout(() => {
      errorContainer.remove();
    }, 5000);
  }

  private async submitForm(data: ContactFormData): Promise<void> {
    const submitButton = this.contactForm.querySelector('button[type="submit"]') as HTMLButtonElement;
    const originalText = submitButton.textContent;

    // Show loading state
    submitButton.textContent = 'Sending...';
    submitButton.disabled = true;

    try {
      // Send to backend API
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to send message');
      }

      // Track successful form submission
      Analytics.trackContactFormSubmit(true);

      this.showSuccessMessage();
      this.contactForm.reset();

    } catch (error) {
      console.error('Form submission error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message. Please try again.';

      // Track failed form submission
      Analytics.trackContactFormSubmit(false, errorMessage);

      this.showErrorMessage(errorMessage);
    } finally {
      submitButton.textContent = originalText;
      submitButton.disabled = false;

      // Reset Turnstile widget for next submission
      try {
        if (typeof turnstile !== 'undefined') {
          turnstile.reset();
        }
      } catch {
        // Turnstile may not be loaded
      }
    }
  }

  private showSuccessMessage(): void {
    const successMessage = document.createElement('div');
    successMessage.className = 'alert alert-success mt-3';
    successMessage.innerHTML = '<strong>Success!</strong> Your message has been sent successfully.';
    
    this.contactForm.appendChild(successMessage);
    
    setTimeout(() => {
      successMessage.remove();
    }, 5000);
  }

  private showErrorMessage(message: string): void {
    const errorMessage = document.createElement('div');
    errorMessage.className = 'alert alert-danger mt-3';
    errorMessage.textContent = message;
    
    this.contactForm.appendChild(errorMessage);
    
    setTimeout(() => {
      errorMessage.remove();
    }, 5000);
  }

  private setupAnimations(): void {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    // Observe elements for animation
    const animatedElements = document.querySelectorAll('.skill-item, .project-card, .section-title, .timeline-item, .education-item, .summary-content');
    animatedElements.forEach(el => {
      observer.observe(el);
    });
  }

  private setupProjectModal(): void {
    // Add click event listeners to project detail buttons
    const projectDetailButtons = document.querySelectorAll('.project-detail-btn');
    projectDetailButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const projectCard = (e.target as HTMLElement).closest('.project-card') as HTMLElement;
        const projectId = projectCard.dataset.project;
        if (projectId) {
          this.openProjectModal(projectId);
        }
      });
    });

    // Track "Live Demo" link clicks in project cards
    const liveDemoLinks = document.querySelectorAll('.project-card a[href][target="_blank"]');
    liveDemoLinks.forEach(link => {
      link.addEventListener('click', () => {
        const projectCard = (link as HTMLElement).closest('.project-card') as HTMLElement;
        const projectId = projectCard?.dataset.project;
        if (projectId) {
          const project = this.projectData.find(p => p.id === projectId);
          if (project) {
            Analytics.trackProjectView(project.title, 'visit_live');
          }
        }
      });
    });

    // Add close modal functionality
    const closeButton = this.projectModal.querySelector('.project-modal-close');
    closeButton?.addEventListener('click', () => {
      this.closeProjectModal();
    });

    // Close modal when clicking outside
    this.projectModal.addEventListener('click', (e) => {
      if (e.target === this.projectModal) {
        this.closeProjectModal();
      }
    });

    // Close modal with Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.projectModal.style.display === 'block') {
        this.closeProjectModal();
      }
    });
  }

  private openProjectModal(projectId: string): void {
    const project = this.projectData.find(p => p.id === projectId);
    if (!project) return;

    // Track project detail view
    Analytics.trackProjectView(project.title, 'view_details');

    const modalBody = this.projectModal.querySelector('.project-modal-body');
    if (!modalBody) return;

    modalBody.innerHTML = this.generateProjectModalContent(project);
    this.projectModal.style.display = 'block';
    document.body.style.overflow = 'hidden';

    // Setup analytics tracking for modal links
    this.setupModalLinkTracking(project);

    // Animate in
    setTimeout(() => {
      this.projectModal.style.opacity = '1';
    }, 10);
  }

  private setupModalLinkTracking(project: ProjectData): void {
    // Track Live Demo clicks from modal
    const liveDemoLink = this.projectModal.querySelector('a[href*="' + project.liveDemo + '"]');
    liveDemoLink?.addEventListener('click', () => {
      Analytics.trackProjectView(project.title, 'visit_live');
    });

    // Track GitHub repo clicks from modal
    if (project.githubRepo) {
      const githubLink = this.projectModal.querySelector('a[href*="github"]');
      githubLink?.addEventListener('click', () => {
        Analytics.trackExternalLink(project.githubRepo || '', 'GitHub Repository', 'project_modal');
      });
    }
  }

  private closeProjectModal(): void {
    this.projectModal.style.display = 'none';
    document.body.style.overflow = 'auto';
  }

  private generateProjectModalContent(project: ProjectData): string {
    return `
      <div class="project-detail-header mb-4">
        <h2 class="project-detail-title">${project.title}</h2>
        <p class="project-detail-description">${project.longDescription}</p>
      </div>

      <div class="row mb-4">
        <div class="col-md-6">
          <h4 class="section-subtitle">Technologies Used</h4>
          <div class="tech-stack">
            ${project.technologies.map(tech => `<span class="tech-tag">${tech}</span>`).join('')}
          </div>
        </div>
        <div class="col-md-6">
          <h4 class="section-subtitle">Project Links</h4>
          <div class="project-links">
            ${project.liveDemo ? `<a href="${project.liveDemo}" target="_blank" class="btn btn-accent me-2 mb-2">Live Demo</a>` : ''}
            ${project.githubRepo ? `<a href="${project.githubRepo}" target="_blank" class="btn btn-outline-light mb-2">GitHub Repository</a>` : ''}
          </div>
        </div>
      </div>

      <div class="project-detail-section mb-4">
        <h4 class="section-subtitle">Key Features</h4>
        <ul class="feature-list">
          ${project.features.map(feature => `<li>${feature}</li>`).join('')}
        </ul>
      </div>

      <div class="project-detail-section mb-4">
        <h4 class="section-subtitle">Challenges & Solutions</h4>
        <ul class="feature-list">
          ${project.challenges.map(challenge => `<li>${challenge}</li>`).join('')}
        </ul>
      </div>

      <div class="project-detail-section">
        <h4 class="section-subtitle">Results & Impact</h4>
        <ul class="feature-list">
          ${project.outcomes.map(outcome => `<li>${outcome}</li>`).join('')}
        </ul>
      </div>
    `;
  }

}

// Initialize the portfolio site when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new PortfolioSite();
});

// Add CSS animation classes dynamically
const animationStyles = `
  .skill-item, .project-card, .section-title {
    opacity: 0;
    transform: translateY(30px);
    transition: all 0.6s ease-out;
  }
  
  .animate-in {
    opacity: 1 !important;
    transform: translateY(0) !important;
  }
  
  .skill-item.animate-in {
    transition-delay: calc(var(--animation-order, 0) * 0.1s);
  }
`;

// Inject animation styles
const styleSheet = document.createElement('style');
styleSheet.textContent = animationStyles;
document.head.appendChild(styleSheet);

// Add animation order to skill items
document.addEventListener('DOMContentLoaded', () => {
  const skillItems = document.querySelectorAll('.skill-item');
  skillItems.forEach((item, index) => {
    (item as HTMLElement).style.setProperty('--animation-order', index.toString());
  });
});